import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FiLock, FiZap, FiArrowRight } from 'react-icons/fi';
import { Card } from '../common/Card';

export function PointsUnlockCard({
  isAuthRequired,
  isMembershipRequired,
  pointsUnlocked,
  currentTier,
  pricePoints,
  balance,
  loading,
  purchaseLoading,
  onUnlock,
  onUpgrade,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!isAuthRequired && !isMembershipRequired && !pointsUnlocked) return null;
  if (currentTier && currentTier !== 'free' && isMembershipRequired) return null;
  if (pointsUnlocked) {
    return (
      <Card className="border border-positive/30 bg-positive/5">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-positive/20 text-positive flex items-center justify-center">
            <FiZap />
          </div>
          <div>
            <div className="text-sm font-semibold text-positive">
              {t('points.unlocked', '已使用点数解锁')}
            </div>
            <div className="text-xs text-slate-400">
              {t('points.unlockedHint', '本场比赛已解锁，重复访问不会扣点')}
            </div>
          </div>
        </div>
      </Card>
    );
  }

  if (isAuthRequired) {
    return (
      <Card className="border border-accent/20 bg-surface/60">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-accent/15 text-accent flex items-center justify-center">
              <FiLock />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">
                {t('points.loginToUse', '登录后使用点数解锁')}
              </div>
              <div className="text-xs text-slate-400">
                {t('points.loginToUseHint', '登录后查看点数余额并解锁比赛内容')}
              </div>
            </div>
          </div>
          <button
            className="inline-flex items-center gap-2 rounded-lg bg-accent/80 px-3 py-2 text-xs font-semibold text-white hover:bg-accent"
            onClick={() => navigate('/login')}
          >
            {t('auth.login', '立即登录')}
            <FiArrowRight />
          </button>
        </div>
      </Card>
    );
  }

  if (!isMembershipRequired) return null;

  const safeBalance = typeof balance === 'number' ? balance : 0;
  const safePrice = typeof pricePoints === 'number' ? pricePoints : null;
  const canUnlock = safePrice !== null && safeBalance >= safePrice;
  const priceText = safePrice !== null ? safePrice : '-';
  const balanceText = loading ? '...' : safeBalance;

  return (
    <Card className="border border-amber-400/20 bg-surface/70">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-full bg-amber-400/15 text-amber-200 flex items-center justify-center">
            <FiZap />
          </div>
          <div>
            <div className="text-sm font-semibold text-white">
              {t('points.unlockTitle', '点数解锁本场比赛')}
            </div>
            <div className="text-xs text-slate-400">
              {t('points.unlockDesc', '使用点数查看 AI 预测与专业数据')}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="rounded-lg border border-white/10 bg-surface/70 px-3 py-2 text-xs text-slate-300">
            {t('points.balance', '点数余额')}: <span className="text-white">{balanceText}</span>
          </div>
          <div className="rounded-lg border border-white/10 bg-surface/70 px-3 py-2 text-xs text-slate-300">
            {t('points.price', '解锁价格')}: <span className="text-white">{priceText}</span>
          </div>
          <button
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold ${
              canUnlock ? 'bg-amber-400 text-slate-900 hover:bg-amber-300' : 'bg-slate-600 text-slate-300'
            }`}
            onClick={onUnlock}
            disabled={!canUnlock || purchaseLoading || loading}
          >
            {purchaseLoading
              ? t('points.unlocking', '解锁中...')
              : t('points.unlockCta', `使用${priceText}点解锁`, { points: priceText })}
          </button>
          <button
            className="inline-flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-xs text-slate-200 hover:bg-white/10"
            onClick={onUpgrade}
          >
            {t('membership.upgradeNow', '立即升级')}
          </button>
          {!canUnlock && !loading && (
            <span className="text-xs text-amber-200">
              {t('points.insufficient', '点数不足')}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}
